import { useContext, useEffect, useMemo, useState } from "react"
import QuestionMeta from "../dashboard/QuestionMeta"
import MsiEffectTitle from "../dashboard/MsiEffectTitle"
import LiveReportRenderContext from "../../StateManagment/LiveReportRenderContext"
import { useFirestoreGeneral } from "../../firebase/useFirestore"
import DataType from '../graphData/DataType'
import useChartColors from '../../hooks/useChartColors'
import TemplateIndicatorChart from '../liveReport/reportBuilder2/widgets/templateRenderers/TemplateIndicatorChart'
import CardHeaderInfoPopover from '../common/CardHeaderInfoPopover'
import ProjectsBreakdownSection from '../common/ProjectsBreakdownSection'
import useHierarchyData from '../../hooks/useHierarchyData'
import defaultDates from '../../utils/defaultDates'
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis'
import summarizeRespondentsByMoment from '../../helpers/dataHelpers/summarizeRespondentsByMoment'
import momentTitlesForRecords from '../../helpers/dataHelpers/momentTitlesForRecords'
import ResponsesSummarySection from '../common/ResponsesSummarySection'
import toDisplayDate from '../../helpers/liveReportHelpers/toDisplayDate'

/**
 * @param {object} props
 * @param {string} props.fieldType
 * @param {string} props.fieldId
 * @param {string | string[]} [props.fieldEffectId] - For MSI-effect fields, the linked `OutputEffects` ID(s); used to show the effect name above the chart.
 * @param {string | string[]} props.researchId
 * @param {string} [props.researchTitle] - Research title(s) shown in the Details content
 * @param {string|Date} [props.startDate] - Start of the period (yyyy-mm-dd or Date); omit for all data
 * @param {string|Date} [props.endDate] - End of the period (yyyy-mm-dd or Date); omit for all data
 * @param {string} [props.personaId]
 * @param {'graph' | 'text' | 'both'} [props.paragrapgMomentVisibilityStyle]
 * @param {string} [props.graphType]
 * @param {Array<string>} [props.colorPalette]
 * @param {Object<string, string>} [props.colorOverrides]
 * @param {React.ReactNode} [props.dashboardOptions]
 * @returns
 */
const ResearchData = ({fieldType, fieldId, fieldEffectId, fieldQuestion, fieldStandard, researchId, researchTitle, startDate, endDate, personaId, paragrapgMomentVisibilityStyle, graphType, graphColor, colorPalette, colorOverrides, dashboardOptions, templateData}) => {

  const inLiveReport = useContext(LiveReportRenderContext);

  // Buiten een live rapportage komt de kleur uit het vaste palet, dus de
  // primaire kaartkleur die hier ooit gekozen is telt niet meer mee.
  const { overrides } = useChartColors({ colorPalette, colorOverrides });
  // State
  const [numberOfMeasureMoments, setNumberOfMeasureMoments] = useState('')
  const [type, setType] = useState('')
  const [momentId, setMomentId] = useState('')

  // Firestore
  const measureMomentsRaw = useFirestoreGeneral('MeasureMoments', 'ResearchID', researchId ? researchId : '')
  const measureMoments = useMemo(() => (measureMomentsRaw || []).filter((m) => !m.Deleted), [measureMomentsRaw])
  const matrixQuestionRows = useFirestoreGeneral('MatrixQuestionRows', 'Field', fieldId ? fieldId : '')


  // Get number of measure moments
  useEffect(() => {
      if(measureMoments){
          setNumberOfMeasureMoments(measureMoments.length)
      }
  }, [measureMoments])

  // Get type of moment
  useEffect(() => {
      if(numberOfMeasureMoments === 1){
          setType(`${fieldType}-moment`)
          setMomentId(measureMoments[0].ID)
      } else if(numberOfMeasureMoments > 1){
          setType(`${fieldType}-development`)
      } else {
          setType('output')
      }
  }, [numberOfMeasureMoments])

  // Mirror the chart's hierarchy fetch (same params → cache hit, no extra
  // Cloud Function call) so the popover sees the same records — enriched with
  // MomentPosition — on every tenant. The per-project breakdown still renders
  // portfolio-only (it returns null elsewhere), but the per-moment
  // respondents/answers need these records on project tenants too.
  const { defaultStartDate, defaultEndDate } = useMemo(() => defaultDates(), [])
  const startMs = useMemo(
      () => toMillis(startDate) ?? toMillis(defaultStartDate) ?? 0,
      [startDate, defaultStartDate]
  )
  const endMs = useMemo(
      () => toMillis(endDate) ?? toMillis(defaultEndDate) ?? 8640000000000000,
      [endDate, defaultEndDate]
  )
  const popoverSyncType = (fieldType === 'matrix-one' || fieldType === 'matrix-multiple') && fieldStandard === 'true'
      ? 'MSIEffect'
      : 'Question'
  const {
      hierarchy: popoverHierarchy,
      records: popoverRecords,
      loading: popoverLoading,
      error: popoverError,
  } = useHierarchyData({
      syncType: popoverSyncType,
      id: fieldId,
      startDate: startMs,
      endDate: endMs,
      researchIds: researchId ? [researchId] : [],
      personaId: personaId || undefined,
      filterByResearchId: true,
  })

  // Position → title / id → position maps for the per-moment breakdown.
  const { id2pos, pos2title } = useMemo(() => {
      const id2posMap = {}
      const pos2titleMap = {}
      ;(Array.isArray(measureMoments) ? measureMoments : []).forEach((m) => {
          const pos = typeof m?.Position === 'number' ? m.Position : parseInt(m?.Position, 10)
          if (Number.isNaN(pos)) return
          id2posMap[m.ID] = pos
          if (m?.Title && pos2titleMap[pos] === undefined) pos2titleMap[pos] = m.Title
      })
      // Meerdere onderzoeken → alleen posities; zie momentTitlesForRecords.
      return { id2pos: id2posMap, pos2title: momentTitlesForRecords(pos2titleMap, popoverRecords) }
  }, [measureMoments, popoverRecords])

  // Respondents + answers (per moment when >1) from the hierarchy records
  // (includes synced child projects on portfolio tenants).
  const responseSummary = useMemo(
      () => summarizeRespondentsByMoment(popoverRecords, { id2pos, pos2title }),
      [popoverRecords, id2pos, pos2title]
  )

  // Popover content: research info rows + per-project breakdown (portfolio only).
  const typeSetting = () => {
      if (measureMoments && measureMoments.length === 1) return 'Meetmoment'
      if (measureMoments && measureMoments.length > 1) return 'Ontwikkeling'
      return 'Outputresultaat'
  }

  const popoverContent = (
      <div>
          <div className="info-popover-row">
              <span className="info-popover-row-label">Onderzoek</span>
              <span className="info-popover-row-value">{researchTitle || '—'}</span>
          </div>
          <div className="info-popover-row">
              <span className="info-popover-row-label">Type</span>
              <span className="info-popover-row-value">{typeSetting()}</span>
          </div>
          {(startDate || endDate) && (
              <div className="info-popover-row">
                  <span className="info-popover-row-label">Periode</span>
                  <span className="info-popover-row-value">
                      {toDisplayDate(startDate) || '—'} – {toDisplayDate(endDate) || '—'}
                  </span>
              </div>
          )}
          <ResponsesSummarySection
              respondents={responseSummary.totalRespondents}
              answers={responseSummary.totalAnswers}
              perMoment={responseSummary.perMoment}
              loading={popoverLoading}
              error={popoverError}
          />
          <ProjectsBreakdownSection hierarchy={popoverHierarchy} valueLabel="Antwoorden" />
      </div>
  )

  return (
      <div className="home-kpi-chart-container">
          {!inLiveReport && (
          <div className="home-kpi-description-container">
              {templateData
                  ? (fieldQuestion && <p>{fieldQuestion}</p>)
                  : (
                      <>
                          <QuestionMeta field={fieldId}/>
                          <MsiEffectTitle effectId={fieldEffectId} />
                      </>
                  )
              }

               {!templateData && (
                <CardHeaderInfoPopover label="Details" content={popoverContent} />
               )}
          </div>
          )}
          {templateData ? (
              <TemplateIndicatorChart
                  fieldId={fieldId}
                  templateData={templateData}
                  graphType={graphType}
                  graphColor={graphColor}
                  colorPalette={colorPalette}
              />
          ) : (
              <DataType
              type={fieldType}
              momentId={momentId}
              fieldId={fieldId}
              startDate={startDate}
              endDate={endDate}
              researchId={researchId}
              graphType={graphType}
              graphColor={graphColor}
              personaId={personaId}
              paragrapgMomentVisibilityStyle={paragrapgMomentVisibilityStyle}
              matrixQuestionRows={matrixQuestionRows}
              filterByResearchId={true}
              colorPalette={colorPalette}
              colorOverrides={colorOverrides}
              primaryColorOverride={overrides?.primary}
              />
          )}
          {dashboardOptions}
      </div>
  )
}

export default ResearchData
